import React, { useEffect, useRef } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { DocumentCategory } from 'enums/DocumentCategory';
import { useAppSelector } from 'hooks/useAppSelector';
import { getDocumentsByCategory, isDocumentCategoryCompleted, isDocumentCategoryRequired } from 'utils/documentsUtils';
import UploadDocumentsCategory from 'components/LoanPortal/UploadDocuments/UploadDocumentCategory';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getErrorMessageForRequiredFields, maskSSN, removeNonDigits, removeSpaces } from 'utils/stringUtils';
import { getMessageForInvalidFields, isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { checkBirthDate } from 'utils/dateUtils';
import { MASKED_SSN_LENGTH, SSN_MAX_LENGTH } from 'utils/constants';
import { LoadingType, setLoadingState } from 'actions';
import { DashboardTabName } from 'enums/DashboardTabName';
import { setLoanPortal } from 'actions/dashboardPortal';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import { setCoOwner } from 'actions/coOwnerInfo';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import NumberInput from 'components/Inputs/NumberInput';
import Loader from 'components/Loader';
import Section from 'components/LoanPortal/Sections/Section';
import SubSection, { SubSectionState } from 'components/LoanPortal/Sections/SubSection/SubSection';
import { Button } from 'components/Buttons/Button';
import { coOwnersApi } from 'api/CoOwner/CoOwnersApi';

import styles from '../InformationTabs.module.scss';
import { CoOwnerInputLabel, CoOwnerInputName } from './HomeOwnershipTab.enums';

const HomeOwnershipTab = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(
    (state) => state.loading[`${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`],
  );
  const coOwnerState = useAppSelector((state) => state.coOwnerInfo);
  const application = useAppSelector((state) => state.application);
  const { email: borrowerEmail } = useAppSelector((state) => state.borrower);
  const { email: coBorrowerEmail } = useAppSelector((state) => state.coBorrower);
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const { isThereNonBorrowingCoOwner, homeOwnershipInformationComplete } = useAppSelector(
    (state) => state.dashboardPortal,
  );
  const homeOwnershipSectionRolledUp = useRef(homeOwnershipInformationComplete);

  const { register, trigger, handleSubmit, watch, formState, setValue, clearErrors, ...methods } = useForm<any>({
    defaultValues: {
      [CoOwnerInputName.FirstName]: coOwnerState.firstName,
      [CoOwnerInputName.LastName]: coOwnerState.lastName,
      [CoOwnerInputName.Address]: coOwnerState.address,
      [CoOwnerInputName.City]: coOwnerState.city,
      [CoOwnerInputName.State]: coOwnerState.state,
      [CoOwnerInputName.Zip]: coOwnerState.zip,
      [CoOwnerInputName.PhoneNumber]: coOwnerState.phoneNumber || '',
      [CoOwnerInputName.Email]: coOwnerState.email,
      [CoOwnerInputName.DateOfBirth]: coOwnerState.dateOfBirth,
      [CoOwnerInputName.Ssn]: coOwnerState.ssn,
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(CoOwnerInputName.Email, {
      required: getErrorMessageForRequiredFields(CoOwnerInputLabel.Email),
      validate: {
        validEmail: (value) => isValidEmail(value) || 'Please enter a valid email address',
        notEqualBorrower: (value) => value !== borrowerEmail || 'Must be different from Borrower Email',
        notEqualCoBorrower: (value) => value !== coBorrowerEmail || 'Must be different from Co-Borrower Email',
      },
    });
    register(CoOwnerInputName.PhoneNumber, {
      required: getErrorMessageForRequiredFields(CoOwnerInputLabel.PhoneNumber),
      validate: (value) => validatePhoneNumber(value),
    });
    register(CoOwnerInputName.DateOfBirth, {
      required: getErrorMessageForRequiredFields(CoOwnerInputLabel.DateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(CoOwnerInputName.Ssn, {
      required: getErrorMessageForRequiredFields(CoOwnerInputLabel.Ssn),
      validate: (value) => value.length === SSN_MAX_LENGTH || getMessageForInvalidFields(CoOwnerInputLabel.Ssn),
    });
  }, [clearErrors, register, watcher]);

  const onSubmit = handleSubmit(async () => {
    try {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`,
          loading: true,
        }),
      );

      await coOwnersApi.updateInformation({
        applicationId: application.applicationId!,
        dateOfBirth: watcher[CoOwnerInputName.DateOfBirth]!,
        phoneNumber: watcher[CoOwnerInputName.PhoneNumber]!,
        ssn: watcher[CoOwnerInputName.Ssn]!,
        email: watcher[CoOwnerInputName.Email]!,
      });

      dispatch(
        setCoOwner({
          email: watcher[CoOwnerInputName.Email],
          phoneNumber: watcher[CoOwnerInputName.PhoneNumber],
          dateOfBirth: watcher[CoOwnerInputName.DateOfBirth],
          ssn: watcher[CoOwnerInputName.Ssn],
        }),
      );

      dispatch(
        setLoanPortal({
          homeOwnershipInformationComplete: true,
        }),
      );
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.HomeOwnership}`,
          loading: false,
        }),
      );
    }
  });

  const coOwnerSubSectionState: SubSectionState = {
    isExist: isThereNonBorrowingCoOwner,
    isCompleted: homeOwnershipSectionRolledUp.current!,
  };

  const documentsSubSectionState: SubSectionState = {
    isExist: isDocumentCategoryRequired(requiredDocuments, DocumentCategory.HomeVerification),
    isCompleted: isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.HomeVerification),
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.requiredInformationContainer}>
          <FormProvider
            register={register}
            trigger={trigger}
            handleSubmit={handleSubmit}
            formState={formState}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            {...methods}
          >
            <form onSubmit={onSubmit}>
              <Section
                title={'Home Verification'}
                subSectionStates={[coOwnerSubSectionState, documentsSubSectionState]}
              >
                <SubSection state={coOwnerSubSectionState}>
                  <div>
                    <div className={styles.sectionHeader}>Non-Borrowing Co-Owner Information</div>
                    <div className={styles.sectionTitle}>
                      Information for the "non-borrowing co-owner" of the subject property is needed to verify their
                      identity and provide them with a UCC-1 acknowledgement form to sign prior to loan closing. Please
                      provide the following information for {coOwnerState.firstName} {coOwnerState.lastName}.
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.FirstName}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.FirstName]}
                        disabled
                      />
                      <Input
                        label={CoOwnerInputLabel.LastName}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.LastName]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.Address}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.Address]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <div className={styles.largeInputContainer}>
                        <Input
                          label={CoOwnerInputLabel.City}
                          className={styles.largeInputContainer}
                          value={watcher[CoOwnerInputName.City]}
                          disabled
                        />
                      </div>
                      <Input
                        label={CoOwnerInputLabel.State}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.State]}
                        disabled
                      />
                      <Input
                        label={CoOwnerInputLabel.Zip}
                        className={styles.formInput}
                        value={watcher[CoOwnerInputName.Zip]}
                        disabled
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <Input
                        label={CoOwnerInputLabel.Email}
                        errorMessage={formState.errors[CoOwnerInputName.Email]?.message}
                        className={styles.formInput}
                        name={CoOwnerInputName.Email}
                        onChange={(event: any) => {
                          clearErrors([CoOwnerInputName.Email]);

                          setValue(CoOwnerInputName.Email, removeSpaces(event.target.value?.toLowerCase()));

                          if (formState.isSubmitted) {
                            trigger(CoOwnerInputName.Email);
                          }
                        }}
                        value={watcher[CoOwnerInputName.Email]}
                        disabled={homeOwnershipInformationComplete}
                      />
                      <PhoneNumberInput
                        label={CoOwnerInputLabel.PhoneNumber}
                        errorMessage={formState.errors[CoOwnerInputName.PhoneNumber]?.message}
                        className={styles.formInput}
                        name={CoOwnerInputName.PhoneNumber}
                        onChange={(value: string) => {
                          clearErrors([CoOwnerInputName.PhoneNumber]);

                          setValue(CoOwnerInputName.PhoneNumber, value);

                          if (formState.isSubmitted) {
                            trigger(CoOwnerInputName.PhoneNumber);
                          }
                        }}
                        value={watcher[CoOwnerInputName.PhoneNumber]}
                        disabled={homeOwnershipInformationComplete}
                      />
                    </div>
                    <div className={styles.rowContainer}>
                      <NumberInput
                        label={CoOwnerInputLabel.DateOfBirth}
                        placeholder="MM/DD/YYYY"
                        errorMessage={formState.errors[CoOwnerInputName.DateOfBirth]?.message}
                        className={styles.formInput}
                        name={CoOwnerInputName.DateOfBirth}
                        onChange={(event) => {
                          clearErrors(CoOwnerInputName.DateOfBirth);

                          setValue(CoOwnerInputName.DateOfBirth, event.target.value);

                          if (formState.isSubmitted) {
                            trigger(CoOwnerInputName.DateOfBirth);
                          }
                        }}
                        format="##/##/####"
                        value={watcher[CoOwnerInputName.DateOfBirth]}
                        mask="_"
                        allowEmptyFormatting
                        disabled={homeOwnershipInformationComplete}
                      />
                      <Input
                        label={CoOwnerInputLabel.Ssn}
                        className={styles.formInput}
                        name={CoOwnerInputName.Ssn}
                        maxLength={MASKED_SSN_LENGTH}
                        onChange={(event) => {
                          const value = removeNonDigits(event.target.value.trim());

                          setValue(CoOwnerInputName.Ssn, value);

                          if (formState.isSubmitted) {
                            trigger(CoOwnerInputName.Ssn);
                          }
                        }}
                        errorMessage={formState.errors[CoOwnerInputName.Ssn]?.message}
                        value={maskSSN(watcher[CoOwnerInputName.Ssn])}
                        inputMode="numeric"
                        disabled={homeOwnershipInformationComplete}
                      />
                    </div>
                  </div>
                  {!homeOwnershipInformationComplete && (
                    <Button className={styles.submitButton} onClick={onSubmit}>
                      Save
                    </Button>
                  )}
                </SubSection>
                <SubSection state={documentsSubSectionState}>
                  <UploadDocumentsCategory
                    title={coOwnerSubSectionState.isExist ? 'Documents Upload' : ''}
                    documents={getDocumentsByCategory(requiredDocuments, DocumentCategory.HomeVerification)}
                    category={DocumentCategory.HomeVerification}
                  />
                </SubSection>
              </Section>
            </form>
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default HomeOwnershipTab;
